import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Upload,
    Scissors,
    AlertTriangle,
    Loader2,
    Clapperboard,
    Type,
    Check,
    Music,
    RefreshCw,
    Volume2,
    Rocket,
} from "lucide-react";
import { NeonBackground } from "@/components/reelflo/NeonBackground";
import { AppShell } from "@/components/reelflo/AppShell";
import { ProcessingTray } from "@/components/reelflo/ProcessingTray";
import { UsageMeter } from "@/components/reelflo/UsageMeter";
import { WorkflowPresetControl } from "@/components/reelflo/WorkflowPresetControl";
import { FirstRunTemplateWizard } from "@/components/reelflo/FirstRunTemplateWizard";
import { MusicTrackList } from "@/components/reelflo/MusicTrackList";
import { CAPTION_STYLES, CaptionSwatch, DEFAULT_CAPTION_TEMPLATE } from "@/components/reelflo/CaptionStyleGrid";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import axios from "axios";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";

// WO#9 — Upload-readiness guard. Single source of truth for whether an upload/
// record action may proceed. Music is the ONLY current blocker (captions, B-roll,
// and pause controls all have valid defaults). Pure function (no side effects) so
// it is trivially testable; extend later by adding checks and returning the first
// blocking reason.
const MUSIC_BLOCK_REASONS = {
    loading: "Music library is still loading.",
    error: "Music library could not load. Try again.",
    noTrack: "Select a music track to continue.",
};

const computeUploadReadiness = ({ musicEnabled, musicSelectedId, musicStatus }) => {
    // Music OFF never requires a track.
    if (!musicEnabled) return { ok: true, reason: null };
    const { loading = false, error = false, loadedOnce = false } = musicStatus || {};
    if (error) return { ok: false, reason: MUSIC_BLOCK_REASONS.error };
    if (loading || !loadedOnce) return { ok: false, reason: MUSIC_BLOCK_REASONS.loading };
    // Require an EXPLICIT selection — a track is never auto-selected.
    if (!musicSelectedId) return { ok: false, reason: MUSIC_BLOCK_REASONS.noTrack };
    return { ok: true, reason: null };
};

// Not-Yet onboarding gate — decides what a fully-live, wizard_seen creator sees:
// the full workspace, a loading placeholder (no flash), a failed-load error
// state, or the simple pre-template screen. Any non-gated user (not yet live, or
// wizard_seen !== true) always gets the full workspace. A creator with >= 1 saved
// template gets the full workspace; a failed reload AFTER a prior success keeps
// the workspace (a failed request never erases already-loaded templates). Pure +
// exported so the decision is trivially testable.
export const computeWorkspaceGate = ({ gateActive, firstLoadDone, error, count }) => {
    if (!gateActive) return "workspace";
    if (!firstLoadDone) return error ? "error" : "loading";
    return count === 0 ? "pretemplate" : "workspace";
};

const ActionButton = ({
    testId,
    icon: Icon,
    title,
    subtitle,
    accent,
    disabled,
    onClick,
    progress,
    uploading,
    ariaDescribedBy,
    breathe,
}) => (
    <motion.button
        type="button"
        data-testid={testId}
        disabled={disabled}
        aria-disabled={disabled}
        aria-describedby={ariaDescribedBy}
        onClick={onClick}
        whileHover={disabled ? undefined : { y: -6 }}
        transition={{ type: "spring", stiffness: 280, damping: 20 }}
        className={[
            "group relative block w-full min-h-[140px] overflow-hidden rounded-lg border p-7 text-left sm:min-h-[160px] sm:p-8",
            "bg-surface-panel",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/70",
            disabled
                ? "cursor-not-allowed border-white/10 opacity-60"
                : "border-purple-400/45 hover:border-purple-400/80",
            !disabled && breathe ? "rf-upload-breathe" : "",
            "transition-[border-color] duration-300",
        ].join(" ")}
    >
        <div className="flex items-center gap-5 sm:gap-6">
            <div
                className="flex h-16 w-16 flex-none items-center justify-center rounded-lg border sm:h-20 sm:w-20"
                style={{
                    borderColor: `${accent}66`,
                    background: `${accent}1f`,
                }}
            >
                <Icon className="h-8 w-8 sm:h-10 sm:w-10" style={{ color: accent }} />
            </div>
            <div className="min-w-0">
                <p className="rf-heading text-2xl font-bold leading-tight text-white sm:text-3xl">
                    {title}
                </p>
                {subtitle ? (
                    <p className="mt-1 truncate text-base text-white/55 sm:text-lg">
                        {subtitle}
                    </p>
                ) : null}
            </div>
        </div>
        {uploading && (
            <div className="mt-5">
                <Progress
                    value={progress}
                    className="h-3 bg-white/10 [&>div]:bg-gradient-to-r [&>div]:from-purple-500 [&>div]:to-cyan-400"
                />
                <p className="mt-2 text-base font-medium text-cyan-200">
                    Uploading… {progress}%
                </p>
            </div>
        )}
    </motion.button>
);

// WO#8 — Curated caption-style catalog + swatch artwork now live in the SHARED
// CaptionStyleGrid module (imported above) so the Dashboard workspace picker and
// the first-run template wizard use ONE definition + one swatch renderer.
// Stable, opaque selection string for the operator-gated FULCRUM custom caption
// theme. Submitted verbatim to the backend as `template_name`; it is NEVER a
// Submagic templateName and NEVER a theme UUID.
const CUSTOM_CAPTION_SELECTION = "custom";

// Schema-version-1 preset validation. PURE + synchronous so an Apply can be
// validated in FULL before any workspace setter runs (all-or-nothing). Rejects
// missing keys, wrong types, out-of-range values, captions outside the current
// catalog, and — once the music library has loaded — a saved track that no
// longer exists. Never coerces values.
const PRESET_SCHEMA_VERSION = 1;
const PRESET_VALID_BROLL_PCT = [25, 50, 75];
const PRESET_VALID_SILENCE_PACE = ["natural", "fast", "extra-fast"];
const PRESET_SETTING_KEYS = [
    "broll_on", "broll_pct", "remove_silence", "silence_pace",
    "caption_template", "music_enabled", "music_selected_id", "music_volume",
];

// Compact inline-editor bounded error (final binding ruling): shown when a
// saved template's snapshot fails STRUCTURAL validation on Edit open / Cancel.
const EDIT_SNAPSHOT_LOAD_ERROR =
    "This template’s saved settings could not be loaded. Nothing was changed.";

const validatePresetSnapshotV1 = (
    s,
    {
        captionCatalog = [],
        musicCatalog = [],
        musicLoadedOnce = false,
        musicLoading = false,
        musicError = false,
    } = {},
) => {
    const bad = (message) => ({ ok: false, message });
    if (!s || typeof s !== "object") return bad("This preset is invalid.");
    for (const k of PRESET_SETTING_KEYS) {
        if (!(k in s)) return bad("This preset is out of date and can't be applied.");
    }
    if (typeof s.broll_on !== "boolean") return bad("This preset is invalid.");
    if (!PRESET_VALID_BROLL_PCT.includes(s.broll_pct)) return bad("This preset is invalid.");
    if (typeof s.remove_silence !== "boolean") return bad("This preset is invalid.");
    if (!PRESET_VALID_SILENCE_PACE.includes(s.silence_pace)) return bad("This preset is invalid.");
    const captions = new Set(captionCatalog);
    if (typeof s.caption_template !== "string" || !captions.has(s.caption_template)) {
        return bad("This preset's caption style is no longer available.");
    }
    if (typeof s.music_enabled !== "boolean") return bad("This preset is invalid.");
    if (!(s.music_selected_id === null || typeof s.music_selected_id === "string")) {
        return bad("This preset is invalid.");
    }
    if (typeof s.music_volume !== "number" || !Number.isFinite(s.music_volume)
        || s.music_volume < 0 || s.music_volume > 100) {
        return bad("This preset is invalid.");
    }
    // Background-music gate — FAIL CLOSED. A music-enabled preset MUST name a
    // saved track: without one it is invalid (no catalog load, no state change).
    // With a saved track the dynamic Submagic catalog MUST be loaded so the
    // track can actually be verified. While it is errored/loading/not-loaded we
    // refuse and flag `needsMusicLoad` so the caller can (re)load the catalog,
    // letting a retry succeed. A preset is NEVER applied on an unverified track.
    if (s.music_enabled) {
        if (s.music_selected_id === null || s.music_selected_id === "") {
            return bad("This preset is invalid.");
        }
        if (musicError) {
            return bad("Your music library couldn't load. Please try again.");
        }
        if (!musicLoadedOnce) {
            return {
                ok: false,
                needsMusicLoad: true,
                message: musicLoading
                    ? "Your music library is still loading. Please try applying again in a moment."
                    : "Loading your music library — please try applying again in a moment.",
            };
        }
        const music = new Set(musicCatalog);
        if (!music.has(s.music_selected_id)) {
            return bad("The saved music track is no longer available. Update the preset.");
        }
    }
    return { ok: true };
};

// Accessible caption-style radio group. Shows the curated eight built-in styles,
// plus one operator-gated FULCRUM custom tile when `customThemeAvailable` is
// true (nine total). Roving tabindex + arrow-key navigation; the selected tile
// carries a check icon and a highlighted ring. The custom tile's stable choice
// string is "custom" (NEVER a theme UUID).
const CaptionStylePicker = ({ value, onChange, disabled, customThemeAvailable, customThemeName }) => {
    const options = customThemeAvailable
        ? [
              ...CAPTION_STYLES,
              Object.freeze({
                  id: "custom",
                  label: customThemeName || "Custom",
                  templateName: CUSTOM_CAPTION_SELECTION,
                  variant: "custom",
              }),
          ]
        : CAPTION_STYLES;
    const idx = Math.max(
        0,
        options.findIndex((s) => s.templateName === value),
    );
    const onKeyDown = (e) => {
        if (disabled) return;
        let next = null;
        if (e.key === "ArrowRight" || e.key === "ArrowDown") {
            next = (idx + 1) % options.length;
        } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
            next = (idx - 1 + options.length) % options.length;
        }
        if (next !== null) {
            e.preventDefault();
            onChange(options[next].templateName);
        }
    };
    return (
        <div
            data-testid="caption-style-picker"
            className="flex flex-col gap-4 rounded-lg border border-white/12 bg-surface-panel p-5 sm:p-6"
        >
            <div className="flex items-center gap-4 sm:gap-5">
                <div
                    className="flex h-12 w-12 flex-none items-center justify-center rounded-lg border sm:h-14 sm:w-14"
                    style={{
                        borderColor: "#A855F766",
                        background: "#A855F71f",
                    }}
                >
                    <Type className="h-6 w-6 text-purple-300 sm:h-7 sm:w-7" />
                </div>
                <div className="min-w-0">
                    <p className="rf-heading text-xl font-bold leading-tight text-white sm:text-2xl">
                        Caption Style
                    </p>
                    <p className="mt-1 text-sm text-white/55 sm:text-base">
                        Pick the caption look — Submagic renders it on your video.
                    </p>
                </div>
            </div>

            <div
                role="radiogroup"
                aria-label="Caption style"
                className="grid grid-cols-2 gap-3 sm:grid-cols-4"
            >
                {options.map((s) => {
                    const selected = s.templateName === value;
                    return (
                        <button
                            key={s.id}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            aria-label={s.label}
                            tabIndex={selected ? 0 : -1}
                            data-testid={`caption-style-${s.id}`}
                            disabled={disabled}
                            onClick={() => onChange(s.templateName)}
                            onKeyDown={onKeyDown}
                            className={[
                                "group flex flex-col rounded-lg border p-2 text-left",
                                "transition-[border-color] duration-200",
                                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/70",
                                "disabled:cursor-not-allowed disabled:opacity-50",
                                selected
                                    ? "border-purple-400/80"
                                    : "border-white/12 hover:border-purple-400/40",
                            ].join(" ")}
                        >
                            <CaptionSwatch variant={s.variant} />
                            <div className="mt-2 flex items-center justify-between gap-2 px-0.5">
                                <span className="truncate text-sm font-semibold text-white/85">
                                    {s.label}
                                </span>
                                {selected && (
                                    <Check
                                        data-testid={`caption-style-check-${s.id}`}
                                        className="h-4 w-4 flex-none text-purple-300"
                                    />
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};


// Optional background-music picker: toggle (default OFF), a paginated My Music
// AUDIO list (fileName + duration + single-player preview + selection + Load
// More), and a 1-100 volume slider (default 30). Selection is lifted to the
// parent (enabled / selectedId / volume); the track list + preview are internal.
const MusicPicker = ({
    enabled, onEnabledChange, selectedId, onSelect, volume, onVolumeChange, disabled,
    onStatusChange, tracks, loading, loadingMore, error, loadedOnce, hasMore,
    onEnsure, onLoadMore, onRefresh, resetSignal,
}) => {
    // WO#9 — report catalog status upward so the parent's upload-readiness guard
    // can block uploads while the library is loading/errored or no track is chosen.
    useEffect(() => {
        onStatusChange?.({ loading, error: !!error, loadedOnce });
    }, [loading, error, loadedOnce, onStatusChange]);

    // Load the library the first time Music is switched ON. Fetch ownership
    // lives in the Dashboard (single entry point); this call deduplicates and
    // no-ops once the catalog has loaded, so a remount never refetches.
    useEffect(() => {
        if (enabled) onEnsure?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled]);

    return (
        <div
            data-testid="music-picker"
            className="flex flex-col gap-4 rounded-lg border border-white/12 bg-surface-panel p-5 sm:p-6"
        >
            <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 sm:gap-5">
                    <div
                        className="flex h-12 w-12 flex-none items-center justify-center rounded-lg border sm:h-14 sm:w-14"
                        style={{
                            borderColor: "#22D3EE66",
                            background: "#22D3EE1f",
                            }}
                    >
                        <Music className="h-6 w-6 text-cyan-300 sm:h-7 sm:w-7" />
                    </div>
                    <div className="min-w-0">
                        <p className="rf-heading text-xl font-bold leading-tight text-white sm:text-2xl">
                            Background Music
                        </p>
                        <p className="mt-1 text-sm text-white/55 sm:text-base">
                            Add a licensed track from your Submagic library.
                        </p>
                    </div>
                </div>
                <Switch
                    checked={enabled}
                    onCheckedChange={onEnabledChange}
                    disabled={disabled}
                    data-testid="music-toggle"
                    aria-label="Toggle background music"
                />
            </div>

            <AnimatePresence initial={false}>
                {enabled && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="flex flex-col gap-4 overflow-hidden"
                    >
                        {/* Loading */}
                        {loading && (
                            <div data-testid="music-loading" className="flex items-center gap-3 py-6 text-white/60">
                                <Loader2 className="h-5 w-5 animate-spin" />
                                <span className="text-sm">Loading your music library…</span>
                            </div>
                        )}

                        {/* Error / refresh-required */}
                        {!loading && error && (
                            <div
                                data-testid="music-error"
                                className="flex flex-col gap-3 rounded-lg border border-amber-400/30 bg-amber-400/5 p-4"
                            >
                                <div className="flex items-start gap-3">
                                    <AlertTriangle className="mt-0.5 h-5 w-5 flex-none text-amber-300" />
                                    <p className="text-sm text-amber-100/90">{error.message}</p>
                                </div>
                                <button
                                    type="button"
                                    data-testid="music-retry-button"
                                    onClick={error.refreshRequired ? onRefresh : () => onEnsure?.({ force: true })}
                                    className="inline-flex items-center gap-2 self-start rounded-lg border border-white/15 px-3 py-1.5 text-sm font-semibold text-white/80 transition-colors duration-200 hover:border-cyan-300/50 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
                                >
                                    <RefreshCw className="h-4 w-4" />
                                    {error.refreshRequired ? "Refresh library" : "Try again"}
                                </button>
                            </div>
                        )}

                        {/* Empty library */}
                        {!loading && !error && loadedOnce && tracks.length === 0 && (
                            <div data-testid="music-empty" className="flex flex-col gap-3 rounded-lg border border-white/12 bg-white/[0.02] p-5 text-center">
                                <p className="text-sm text-white/70">
                                    No AUDIO tracks are available in your Submagic library yet.
                                    Add at least one licensed track, then refresh.
                                </p>
                                <button
                                    type="button"
                                    data-testid="music-refresh-empty"
                                    onClick={onRefresh}
                                    className="inline-flex items-center gap-2 self-center rounded-lg border border-white/15 px-3 py-1.5 text-sm font-semibold text-white/80 transition-colors duration-200 hover:border-cyan-300/50 hover:text-white"
                                >
                                    <RefreshCw className="h-4 w-4" /> Refresh
                                </button>
                            </div>
                        )}

                        {/* Track list + previews (shared MusicTrackList engine). */}
                        {!loading && !error && tracks.length > 0 && (
                            <MusicTrackList
                                tracks={tracks}
                                selectedId={selectedId}
                                onSelect={onSelect}
                                hasMore={hasMore}
                                onLoadMore={onLoadMore}
                                loadingMore={loadingMore}
                                resetSignal={resetSignal}
                                listTestId="music-track-list"
                                rowTestIdPrefix="music-track"
                                previewTestIdPrefix="music-preview"
                                loadMoreTestId="music-load-more"
                                ariaLabel="Music track"
                            />
                        )}

                        {/* Volume */}
                        {!loading && !error && tracks.length > 0 && (
                            <div data-testid="music-volume" className="flex flex-col gap-2 pt-1">
                                <div className="flex items-center justify-between">
                                    <label className="flex items-center gap-2 text-sm font-semibold text-white/80">
                                        <Volume2 className="h-4 w-4 text-cyan-300" /> Music volume
                                    </label>
                                    <span data-testid="music-volume-value" className="text-sm font-semibold text-cyan-200">
                                        {volume}
                                    </span>
                                </div>
                                <Slider
                                    min={1}
                                    max={100}
                                    step={1}
                                    value={[volume]}
                                    onValueChange={(v) => onVolumeChange(v[0])}
                                    aria-label="Music volume"
                                    data-testid="music-volume-slider"
                                />
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};


export default function Dashboard() {
    const { user, refreshUser } = useAuth();
    const navigate = useNavigate();
    const reduce = useReducedMotion();
    const [videos, setVideos] = useState([]);
    const [uploading, setUploading] = useState(null); // { folder, progress }
    const [celebrationId, setCelebrationId] = useState(null);
    // Customer usage meter (GET /api/account/usage). Kept independent of the
    // video workspace: a failure sets usageError but never blocks uploads.
    const [usage, setUsage] = useState(null);
    const [usageError, setUsageError] = useState(false);
    // WO#24: real Submagic B-roll on/off toggle. Default ON (the flagship AI
    // b-roll experience). ON -> folder "auto-broll" + magic_brolls true;
    // OFF -> folder "no-broll" + magic_brolls false.
    const [brollOn, setBrollOn] = useState(true);
    // WO#24b: B-roll AMOUNT (Submagic magicBrollsPercentage). 25/50/75, default
    // 50. Only shown + sent-as-effective when the B-roll toggle is ON.
    const [brollPct, setBrollPct] = useState(50);
    // WO#24c: pause / dead-air removal (Submagic removeSilencePace +
    // removeBadTakes). Toggle default ON; pace default "natural". Pace shown
    // only when the toggle is ON.
    const [removeSilence, setRemoveSilence] = useState(true);
    const [silencePace, setSilencePace] = useState("natural");
    // WO#8: curated caption-style selection. Holds the EXACT Submagic
    // templateName (default Bold Impact / "Hormozi 2"). Sent on the legacy
    // /api/upload FormData as `template_name`.
    const [captionStyle, setCaptionStyle] = useState(DEFAULT_CAPTION_TEMPLATE);
    // WO#9 / My Music V1: background-music selection (default OFF).
    const [musicEnabled, setMusicEnabled] = useState(false);
    const [musicSelectedId, setMusicSelectedId] = useState(null);
    const [musicVolume, setMusicVolume] = useState(30);
    // WO#9 — catalog status lifted from MusicPicker for the upload-readiness guard.
    const [musicStatus, setMusicStatus] = useState({
        loading: false, error: false, loadedOnce: false,
    });
    // WO#9 — available music track objects (lifted from MusicPicker) so a
    // preset's saved track can be validated before Apply AND the first-run
    // template wizard can render the creator's track list. Ids are derived.
    const [availableMusicTracks, setAvailableMusicTracks] = useState([]);
    const availableMusicIds = useMemo(
        () => availableMusicTracks.map((t) => t.userMediaId),
        [availableMusicTracks],
    );
    // WO#9 — monotonically-increasing "please load the catalog" signal. Bumped
    // when a music-enabled preset apply is blocked on the not-yet-loaded catalog
    // so the MusicPicker loads it and a retry can succeed (fail-closed + heal).
    const [musicLoadRequest, setMusicLoadRequest] = useState(0);
    // Catalog pagination + presentation state (fetch ownership moved here from
    // MusicPicker so an unmounting picker can never discard a valid response).
    const [musicHasMore, setMusicHasMore] = useState(false);
    const [musicNextCursor, setMusicNextCursor] = useState(null);
    const [musicLoadingMore, setMusicLoadingMore] = useState(false);
    // Full error detail ({ message, refreshRequired } | null) for the picker UI;
    // musicStatus.error remains the boolean mirror consumed by readiness/wizards.
    // null/absent means "no error to render".
    const [musicErrorDetail, setMusicErrorDetail] = useState(null);
    // Bumped on library refresh so the shared MusicTrackList stops any preview.
    const [musicRefreshSeq, setMusicRefreshSeq] = useState(0);
    // Generation ownership: 0 = no generation has ever started; ids are
    // monotonically increasing integers. Only a settle whose captured id still
    // equals this ref may write shared state; anything else writes NOTHING.
    const musicGenRef = useRef(0);
    // The in-flight catalog run (Promise | null). null/absent = nothing in
    // flight, so an ensure starts a request; otherwise ensure returns this.
    const musicInFlightRef = useRef(null);
    // Synchronous mirror of musicStatus.loadedOnce for the ensure guards.
    const musicLoadedRef = useRef(false);

    const applyMusicPage = useCallback((data, append) => {
        const incoming = Array.isArray(data.tracks) ? data.tracks : [];
        setAvailableMusicTracks((prev) => {
            if (!append) return incoming;
            const seen = new Set(prev.map((t) => t.userMediaId));
            return [...prev, ...incoming.filter((t) => !seen.has(t.userMediaId))];
        });
        setMusicHasMore(!!data.hasMore);
        setMusicNextCursor(data.nextCursor || null);
    }, []);

    const loadFirstPage = useCallback(async (gen) => {
        try {
            const { data } = await api.get("/music/tracks");
            if (gen !== musicGenRef.current) return; // superseded — zero writes
            applyMusicPage(data, false);
            musicLoadedRef.current = true;
            setMusicErrorDetail(null);
            setMusicStatus({ loading: false, error: false, loadedOnce: true });
        } catch (err) {
            if (gen !== musicGenRef.current) return; // superseded — zero writes
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            setMusicErrorDetail({
                message: (detail && detail.message) || detail || "Could not load music.",
                refreshRequired: status === 409,
            });
            setMusicStatus({ loading: false, error: true, loadedOnce: musicLoadedRef.current });
        }
    }, [applyMusicPage]);

    const loadMore = useCallback(async () => {
        if (!musicNextCursor || musicLoadingMore) return;
        const gen = musicGenRef.current; // the page belongs to the current generation
        setMusicLoadingMore(true);
        setMusicErrorDetail(null);
        try {
            const { data } = await api.get("/music/tracks", { params: { cursor: musicNextCursor } });
            if (gen !== musicGenRef.current) return; // superseded — zero writes
            applyMusicPage(data, true);
        } catch (err) {
            if (gen !== musicGenRef.current) return; // superseded — zero writes
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            setMusicErrorDetail({
                message: (detail && detail.message) || detail || "Could not load more tracks.",
                refreshRequired: status === 409,
            });
            setMusicStatus({ loading: false, error: true, loadedOnce: musicLoadedRef.current });
        } finally {
            if (gen === musicGenRef.current) setMusicLoadingMore(false);
        }
    }, [musicNextCursor, musicLoadingMore, applyMusicPage]);

    const refreshMusicLibrary = useCallback(() => {
        const gen = ++musicGenRef.current; // an explicit refresh supersedes
        setMusicRefreshSeq((n) => n + 1);
        setMusicErrorDetail(null);
        setMusicLoadingMore(false);
        setMusicStatus({ loading: true, error: false, loadedOnce: musicLoadedRef.current });
        const run = (async () => {
            try {
                const { data } = await api.post("/music/tracks/refresh");
                if (gen !== musicGenRef.current) return; // superseded — zero writes
                applyMusicPage(data, false);
                musicLoadedRef.current = true;
                setMusicErrorDetail(null);
                setMusicStatus({ loading: false, error: false, loadedOnce: true });
            } catch (err) {
                if (gen !== musicGenRef.current) return; // superseded — zero writes
                const detail = err?.response?.data?.detail;
                setMusicErrorDetail({
                    message: (detail && detail.message) || detail || "Could not refresh music.",
                    refreshRequired: false,
                });
                setMusicStatus({ loading: false, error: true, loadedOnce: musicLoadedRef.current });
            } finally {
                if (musicInFlightRef.current === run) musicInFlightRef.current = null;
            }
        })();
        musicInFlightRef.current = run;
        return run;
    }, [applyMusicPage]);

    // Single catalog-load entry point (reviewer-named; NEW in this change).
    // Every caller — the picker's music toggle, the preset-apply self-heal, and
    // both wizards — funnels through here. Concurrent calls deduplicate onto
    // the one in-flight request; `{ force: true }` (an explicit retry)
    // supersedes the current generation. A null/absent `opts` is a plain
    // ensure: no-op once loaded, joins the in-flight request when one exists.
    const ensureMusicCatalog = useCallback((opts = {}) => {
        const force = opts.force === true;
        if (!force) {
            if (musicLoadedRef.current) return Promise.resolve();
            if (musicInFlightRef.current) return musicInFlightRef.current;
        }
        const gen = ++musicGenRef.current;
        setMusicErrorDetail(null);
        setMusicLoadingMore(false);
        setMusicStatus({ loading: true, error: false, loadedOnce: musicLoadedRef.current });
        const run = loadFirstPage(gen).finally(() => {
            if (musicInFlightRef.current === run) musicInFlightRef.current = null;
        });
        musicInFlightRef.current = run;
        return run;
    }, [loadFirstPage]);

    // WO#9 — applyPreset's fail-closed self-heal bumps musicLoadRequest;
    // consumed HERE (Dashboard-owned) so the load fires even when no
    // MusicPicker is mounted (compact shell). Writes no other state.
    useEffect(() => {
        if (musicLoadRequest > 0) ensureMusicCatalog();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [musicLoadRequest]);

    const fileRef = useRef(null);
    // WO#9 — target for scroll/focus when an upload is blocked by music readiness.
    const musicSectionRef = useRef(null);
    const historyRef = useRef(null);
    // WO#25c — target for "Upload another video" from a failed video row.
    const uploadSectionRef = useRef(null);
    const seenCompletionsRef = useRef(new Set());
    const activated = !!user?.activated;
    // Account activation gate (waiting room). Missing field => ready (grandfathered).
    const ready = user?.account_ready !== false;
    const locked = !ready;
    // Full-power dashboard requires both paid activation and account_ready.
    const live = activated && ready;

    // First-run onboarding. `wizard_seen === false` marks a brand-new creator
    // who has never answered the welcome prompt. (An ABSENT flag — legacy /
    // synthetic states — deliberately does NOT trigger onboarding, preserving
    // backward compatibility.)
    const needsWelcome = live && !!user && user.wizard_seen === false;
    const [welcomeOpen, setWelcomeOpen] = useState(false);
    const [welcomeChoice, setWelcomeChoice] = useState(null); // 'yes' | 'no'
    const [welcomeSaving, setWelcomeSaving] = useState(false);
    const [welcomeError, setWelcomeError] = useState(false);
    const [wizardOpen, setWizardOpen] = useState(false);
    const [presetReloadSignal, setPresetReloadSignal] = useState(0);
    // Retains the id of a first-run template that was CREATED but whose default
    // assignment then failed, so a retry sets ONLY the default (never re-creates).
    const firstRunPresetIdRef = useRef(null);

    // Open the welcome prompt once the creator is eligible. Once wizard_seen
    // flips true (persisted + refreshed) the condition is false and it never
    // reopens.
    useEffect(() => {
        if (needsWelcome) setWelcomeOpen(true);
    }, [needsWelcome]);

    // Not-Yet onboarding gate — a fully-live, wizard_seen creator with ZERO saved
    // templates sees a simple pre-template screen instead of the workspace. Uses
    // the EXISTING presets endpoint (no new backend field). A failed load NEVER
    // erases already-loaded templates (count / firstLoadDone are preserved on a
    // later failure), so a customer with templates keeps the workspace.
    const [presetGate, setPresetGate] = useState({
        loading: false, firstLoadDone: false, error: false, count: 0,
    });
    const gateActive = live && user?.wizard_seen === true;
    const loadPresetGate = useCallback(async () => {
        setPresetGate((g) => ({ ...g, loading: true, error: false }));
        try {
            const { data } = await api.get("/account/workflow-presets");
            const list = (data && data.presets) || [];
            setPresetGate({ loading: false, firstLoadDone: true, error: false, count: list.length });
        } catch (_e) {
            setPresetGate((g) => ({ ...g, loading: false, error: true }));
        }
    }, []);
    useEffect(() => {
        if (gateActive) loadPresetGate();
    }, [gateActive, presetReloadSignal, loadPresetGate]);
    const workspaceGate = computeWorkspaceGate({
        gateActive,
        firstLoadDone: presetGate.firstLoadDone,
        error: presetGate.error,
        count: presetGate.count,
    });

    // ---- Compact / Edit view state (post-template workspace simplification) ----
    // Compact view: an onboarded customer with >= 1 saved template sees the
    // template picker, a small "Edit template" affordance, and one large upload
    // ActionButton. Detailed controls (b-roll, silence, caption, music) are
    // hidden. Legacy customers (wizard_seen !== true) keep the full workspace.
    // Edit view: reveals the same existing detail controls inline (no new
    // components) with a sticky Save / Cancel bar and upload blocked.
    const [templateEditing, setTemplateEditing] = useState(false);
    // `{ id, name, revision, settings, isDefault } | null` — lifted from
    // WorkflowPresetControl via onActiveChange so the compact strip has a
    // template name to display and inline saves have the correct revision.
    const [activeTemplate, setActiveTemplate] = useState(null);
    // Monotonic signal that asks WorkflowPresetControl to run its owner-scoped
    // revision-aware PATCH against the current live settings. Result comes back
    // through onSaveEditsResult below.
    const [saveEditsSignal, setSaveEditsSignal] = useState(0);
    const [saveEditsError, setSaveEditsError] = useState("");
    const [saveEditsBusy, setSaveEditsBusy] = useState(false);
    // Immutable snapshot of the template's SAVED settings captured the moment
    // the compact inline editor opens. Drives Cancel restoration and the
    // "Unsaved changes" comparison. Null whenever the editor is closed.
    const [editBaseline, setEditBaseline] = useState(null);
    const inCompactShell = workspaceGate === "workspace" && gateActive && presetGate.count >= 1;
    const showDetailControls = !inCompactShell || templateEditing;
    // If we leave the compact shell (e.g. templates load into 0 count after a
    // delete, or gateActive flips off), never leave the customer trapped in an
    // orphaned edit state.
    useEffect(() => {
        if (!inCompactShell && templateEditing) {
            setTemplateEditing(false);
            setSaveEditsError("");
            setSaveEditsBusy(false);
            setEditBaseline(null);
        }
    }, [inCompactShell, templateEditing]);

    // Mirrors wizardOpen for in-flight /videos requests (ref, so fetchVideos
    // keeps its dependency list unchanged). A poll that started before the
    // wizard opened but resolves afterward is stale and must not write state.
    const wizardOpenRef = useRef(false);

    const fetchVideos = useCallback(async () => {
        try {
            const { data } = await api.get("/videos");
            // Stale-response guard: never write video state while the first-run
            // wizard is open — the close path performs one fresh fetch instead.
            if (wizardOpenRef.current) return;
            const vids = data.videos || [];

            // Detect newly-completed videos (one-time new_completion flag).
            const fresh = vids.filter(
                (v) => v.new_completion && !seenCompletionsRef.current.has(v.id),
            );

            // Always accept the complete server payload — every changed field
            // (known or future) must reach the UI. An earlier semantic-equality
            // guard here compared only seven fields and suppressed legitimate
            // updates (ready_in_finished_videos, finished_video_url, doc_url,
            // name, folder, thumbnail_color, size, created_at); it was removed.
            setVideos(vids);

            if (fresh.length) {
                // Same-session celebration guard: mark seen so the burst does
                // not replay on later polls. Touches NO server state.
                fresh.forEach((f) => seenCompletionsRef.current.add(f.id));
                // Celebrate the newest one (skip entirely under reduced motion).
                if (!reduce) {
                    setCelebrationId(fresh[0].id);
                    setTimeout(() => setCelebrationId(null), 1900);
                }
            }
        } catch (e) {
            /* silent */
        }
    }, [reduce]);

    // /videos polling — one immediate fetch plus exactly one 4-second interval,
    // suspended ONLY while the first-run template wizard is open. The 4-second
    // poll is runtime-proven to trigger the wizard music-preview cutoff in the
    // controlled base-build experiment (the exact React unmount mechanism
    // remains unproven from source), so the wizard's lifetime is kept free of
    // poll-driven re-renders. Only this read-only GET pauses — backend
    // processing, usage/preset/music loading and uploads are untouched. The
    // welcome prompt, Not-yet screen, compact view and Edit template keep
    // polling as before. Closing the wizard re-enters this effect: one
    // immediate fresh fetch, then a single new interval (cleanup guarantees no
    // duplicate timers across repeated open/close cycles).
    useEffect(() => {
        wizardOpenRef.current = wizardOpen;
        if (wizardOpen) return undefined;
        fetchVideos();
        const id = setInterval(fetchVideos, 4000);
        return () => clearInterval(id);
    }, [wizardOpen, fetchVideos]);

    // Usage meter — fetched once on mount. Isolated from fetchVideos so a meter
    // error never affects the video feed (and vice versa).
    const fetchUsage = useCallback(async () => {
        try {
            const { data } = await api.get("/account/usage");
            setUsage(data);
            setUsageError(false);
        } catch (e) {
            setUsageError(true);
        }
    }, []);

    useEffect(() => {
        fetchUsage();
    }, [fetchUsage]);

    // Handle the return trip from Google OAuth (?google=connected|error|invalid_state).
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const g = params.get("google");
        if (!g) return;
        if (g === "connected") {
            toast.success("Google account connected!");
            refreshUser();
        } else if (g === "invalid_state") {
            toast.error("Google connection failed (security check). Please try again.");
        } else if (g === "error") {
            toast.error("Could not connect Google. Please try again.");
        }
        // Clean the query string without reloading.
        window.history.replaceState({}, "", window.location.pathname);
    }, [refreshUser]);

    // WO#9 — single upload-readiness gate shared by every capture/upload entry.
    const uploadReadiness = computeUploadReadiness({
        musicEnabled, musicSelectedId, musicStatus,
    });

    // Workflow presets — the exact workspace settings a preset captures. Kept in
    // sync with the individual state above so "Save Current" snapshots what the
    // user sees right now.
    const currentSettings = {
        broll_on: brollOn,
        broll_pct: brollPct,
        remove_silence: removeSilence,
        silence_pace: silencePace,
        caption_template: captionStyle,
        music_enabled: musicEnabled,
        music_selected_id: musicSelectedId,
        music_volume: musicVolume,
    };

    // Compact edit-view dirty flag — deep structural comparison across exactly
    // the eight canonical fields against the captured edit baseline. Pristine
    // on open; one divergent field is dirty; reverting it is pristine again.
    const editDirty = !!(templateEditing && editBaseline
        && PRESET_SETTING_KEYS.some(
            (k) => (currentSettings[k] ?? null) !== (editBaseline[k] ?? null)));

    // Apply a preset — ALL-OR-NOTHING. The FULL schema-v1 snapshot is validated
    // (types, ranges, caption catalog, and — once the music library has loaded —
    // the saved track's availability) BEFORE any setter runs. On any failure we
    // toast a clear reason and change NOTHING. A successful apply only mutates
    // local workspace state; it never uploads, creates a job, reserves minutes,
    // or calls Submagic. Returns true only when actually applied.
    const applyPreset = useCallback((s) => {
        const captionCatalog = [
            ...CAPTION_STYLES.map((c) => c.templateName),
            ...(user?.custom_theme_available ? [CUSTOM_CAPTION_SELECTION] : []),
        ];
        const res = validatePresetSnapshotV1(s, {
            captionCatalog,
            musicCatalog: availableMusicIds,
            musicLoadedOnce: !!musicStatus.loadedOnce,
            musicLoading: !!musicStatus.loading,
            musicError: !!musicStatus.error,
        });
        if (!res.ok) {
            // Self-healing: a music-enabled preset with a saved track needs the
            // dynamic catalog. Kick off a load so the customer's retry can verify
            // the track and apply. This changes NO workspace setting.
            if (res.needsMusicLoad) setMusicLoadRequest((n) => n + 1);
            toast.error(res.message);
            return false;
        }
        setBrollOn(s.broll_on);
        setBrollPct(s.broll_pct);
        setRemoveSilence(s.remove_silence);
        setSilencePace(s.silence_pace);
        setCaptionStyle(s.caption_template);
        setMusicEnabled(s.music_enabled);
        setMusicSelectedId(s.music_selected_id);
        setMusicVolume(s.music_volume);
        return true;
    }, [user?.custom_theme_available, availableMusicIds, musicStatus.loadedOnce,
        musicStatus.loading, musicStatus.error]);

    // Compact inline-editor hydrator — STRUCTURAL all-or-nothing (edit-only;
    // final binding ruling). Unlike applyPreset it NEVER consults the dynamic
    // music catalog (loading state or membership): a saved track id is trusted
    // structurally so Edit open / Cancel can hydrate before the catalog loads,
    // and a catalog-missing id stays selected instead of being replaced with
    // defaults. Validates every one of the eight fields BEFORE any setter runs;
    // on any failure it mutates nothing and returns false. Never uploads,
    // PATCHes, toasts, or substitutes a default.
    const hydrateSavedSnapshot = useCallback((s) => {
        if (!s || typeof s !== "object") return false;
        for (const k of PRESET_SETTING_KEYS) {
            if (!(k in s)) return false;
        }
        if (typeof s.broll_on !== "boolean") return false;
        if (!PRESET_VALID_BROLL_PCT.includes(s.broll_pct)) return false;
        if (typeof s.remove_silence !== "boolean") return false;
        if (!PRESET_VALID_SILENCE_PACE.includes(s.silence_pace)) return false;
        const captionCatalog = [
            ...CAPTION_STYLES.map((c) => c.templateName),
            ...(user?.custom_theme_available ? [CUSTOM_CAPTION_SELECTION] : []),
        ];
        if (typeof s.caption_template !== "string"
            || !captionCatalog.includes(s.caption_template)) return false;
        if (typeof s.music_enabled !== "boolean") return false;
        if (!(s.music_selected_id === null || typeof s.music_selected_id === "string")) {
            return false;
        }
        if (typeof s.music_volume !== "number" || !Number.isFinite(s.music_volume)
            || s.music_volume < 0 || s.music_volume > 100) return false;
        if (s.music_enabled
            && (s.music_selected_id === null || s.music_selected_id === "")) return false;
        setBrollOn(s.broll_on);
        setBrollPct(s.broll_pct);
        setRemoveSilence(s.remove_silence);
        setSilencePace(s.silence_pace);
        setCaptionStyle(s.caption_template);
        setMusicEnabled(s.music_enabled);
        setMusicSelectedId(s.music_selected_id);
        setMusicVolume(s.music_volume);
        return true;
    }, [user?.custom_theme_available]);

    // When a capture/upload is attempted while blocked: surface the reason and send
    // the user to the music section. Never mutates any selected/recorded media.
    const announceBlocked = () => {
        if (uploadReadiness.reason) toast.error(uploadReadiness.reason);
        const el = musicSectionRef.current;
        if (el) {
            try {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                el.focus?.();
            } catch (_e) { /* noop */ }
        }
    };

    // "Upload another video" (from a failed row): scroll to and focus the upload
    // section. Does NOT reopen the picker, retry, or touch any selected media.
    const scrollToUpload = useCallback(() => {
        const el = uploadSectionRef.current;
        if (el) {
            try {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                el.focus?.({ preventScroll: true });
            } catch (_e) { /* noop */ }
        }
    }, []);

    // Finished-video handoff: acknowledge (persist server-side) THEN navigate to
    // Finished Videos. Navigation happens whether or not the ack succeeds; on
    // failure we do NOT clear local/server state, so the notice may reappear —
    // that is correct. The local gate is cleared ONLY after server success.
    // Finished-video handoff: navigate to Finished Videos with a refresh-safe
    // deep-link. focus=<finished_video_job_id> (the /api/video-hub job_id) is the
    // exact card to reveal; completion=<videos.id> is the one-time flag to clear.
    // The acknowledgment is deliberately NOT sent here — the Finished Videos page
    // acks ONLY after the target card is rendered and reachable in the DOM, so a
    // click that never reaches a real card can never clear the notice.
    const handleViewFinished = useCallback(
        (video) => {
            const focus = (video?.finished_video_job_id || "").trim();
            const completion = (video?.id || "").trim();
            // Never navigate to a broken deep-link: without a valid target id the
            // CTA is not actionable (the server only sets finished_video_job_id
            // once the video is represented in Finished Videos).
            if (!focus || !completion) return;
            navigate(
                `/videos?focus=${encodeURIComponent(focus)}` +
                    `&completion=${encodeURIComponent(completion)}`,
            );
        },
        [navigate],
    );

    // --- First-run onboarding writes -------------------------------------
    // STEP 1 (Yes OR No): persist wizard_seen through the Commit 1 latch BEFORE
    // the welcome modal closes. On failure keep the modal open and offer a Retry
    // that repeats ONLY this write — never re-asking Yes/No, never creating a
    // preset or a default. Yes opens the wizard ONLY after this succeeds.
    const persistWizardSeen = useCallback(async (choice) => {
        setWelcomeChoice(choice);
        setWelcomeSaving(true);
        setWelcomeError(false);
        try {
            await api.post("/account/workflow-wizard/seen");
            await refreshUser();
            setWelcomeSaving(false);
            setWelcomeOpen(false);
            if (choice === "yes") setWizardOpen(true);
        } catch (e) {
            setWelcomeSaving(false);
            setWelcomeError(true); // keep modal open; show Retry
        }
    }, [refreshUser]);

    const retryWizardSeen = useCallback(() => {
        if (welcomeChoice) persistWizardSeen(welcomeChoice);
    }, [welcomeChoice, persistWizardSeen]);

    // STEP 2 (Yes → wizard finish): create a GENUINELY NEW template (POST), then
    // set it as the default via the Commit 1 route. If the template saved but the
    // default assignment failed, retain its id so the wizard's retry sets ONLY
    // the default (never re-creating the template). wizard_seen is NOT re-posted
    // here — it was already saved on the Yes answer.
    const handleFirstRunFinish = useCallback(async ({ settings, name }) => {
        try {
            let presetId = firstRunPresetIdRef.current;
            if (!presetId) {
                const { data } = await api.post("/account/workflow-presets", { name, settings });
                presetId = data.preset.id;
                firstRunPresetIdRef.current = presetId;
            }
            try {
                await api.put("/account/default-template", { default_template_id: presetId });
            } catch (de) {
                return { ok: false, needDefaultRetry: true, presetId };
            }
            await refreshUser();
            setPresetReloadSignal((n) => n + 1);
            firstRunPresetIdRef.current = null;
            setWizardOpen(false);
            toast.success("Your first template is ready.");
            return { ok: true };
        } catch (e) {
            toast.error(
                e?.response?.data?.detail?.message ||
                    "Couldn't save your template. Try again.",
            );
            return { ok: false };
        }
    }, [refreshUser]);

    const handleFirstRunRetryDefault = useCallback(async (presetId) => {
        try {
            await api.put("/account/default-template", { default_template_id: presetId });
            await refreshUser();
            setPresetReloadSignal((n) => n + 1);
            firstRunPresetIdRef.current = null;
            setWizardOpen(false);
            toast.success("Your first template is ready.");
        } catch (e) {
            toast.error("Couldn't set your default template. Try again.");
        }
    }, [refreshUser]);

    const pick = () => {
        if (!activated || locked) return;
        // Onboarding must be answered/finished before the picker can open.
        if (welcomeOpen || wizardOpen) return;
        // Compact-Dashboard: while the customer is editing their template, the
        // picker refuses to open regardless of other guards. Unsaved edits must
        // be saved (or cancelled) before an upload runs against them.
        if (templateEditing) return;
        // WO#9 guard: never open the file picker/camera while music is not ready.
        if (!uploadReadiness.ok) { announceBlocked(); return; }
        fileRef.current?.click();
    };

    // Fallback path: original streaming route (kept intact on the backend).
    const streamingUpload = async (folder, file, magicBrolls, magicBrollsPct, opts) => {
        const fd = new FormData();
        fd.append("folder", folder);
        fd.append("magic_brolls", String(magicBrolls));
        fd.append("magic_brolls_pct", String(magicBrollsPct));
        fd.append("remove_silence", String(opts.removeSilence));
        fd.append("silence_pace", opts.silencePace);
        // WO#8: forward the chosen Submagic caption-style templateName (exact,
        // case-sensitive). Only appended when present; the backend validates it
        // against a static allowlist and falls back to its default when absent.
        if (opts.templateName) {
            fd.append("template_name", opts.templateName);
        }
        // WO#9: legacy path has no presign-approved record, so it sends the music
        // selection here and the backend validates it CACHE-ONLY before any side
        // effect. OFF -> enabled=false (no music downstream).
        fd.append("music_enabled", String(opts.music.enabled));
        if (opts.music.enabled && opts.music.userMediaId) {
            fd.append("music_user_media_id", opts.music.userMediaId);
        }
        fd.append("music_volume", String(opts.music.volume));
        fd.append("file", file);
        await api.post("/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (p) => {
                const pct = p.total
                    ? Math.round((p.loaded / p.total) * 100)
                    : 0;
                setUploading({ folder, progress: pct });
            },
        });
    };

    // Primary path: direct browser -> R2 via presigned URL (bypasses the
    // platform request-body size limit). presign -> PUT to R2 -> complete.
    const directUpload = async (folder, file, magicBrolls, magicBrollsPct, opts) => {
        const ctype = file.type || "video/mp4";
        // 1) Ask the backend for a presigned PUT URL (auth-gated).
        const { data: presign } = await api.post("/upload/presign", {
            filename: file.name,
            folder,
            content_type: ctype,
            magic_brolls: magicBrolls,
            magic_brolls_pct: magicBrollsPct,
            remove_silence: opts.removeSilence,
            silence_pace: opts.silencePace,
            template_name: opts.templateName,
            music_enabled: opts.music.enabled,
            music_user_media_id: opts.music.userMediaId,
            music_volume: opts.music.volume,
        });
        // 2) PUT the file straight to R2. Raw axios (NOT the `api` instance) so
        //    there's no baseURL and no Authorization header on the cross-origin
        //    request. Content-Type is sent but NOT signed (CORS allows it).
        await axios.put(presign.url, file, {
            headers: { "Content-Type": ctype },
            onUploadProgress: (p) => {
                const pct = p.total
                    ? Math.round((p.loaded / p.total) * 100)
                    : 0;
                setUploading({ folder, progress: pct });
            },
        });
        // 3) Tell the backend to write the video record (the file already sits
        //    in R2, which fires the same external pipeline as the old route).
        //    This body is AUTHORITATIVE for the engine job's magic_brolls value.
        await api.post("/upload/complete", {
            object_key: presign.object_key,
            filename: presign.filename,
            folder,
            content_type: ctype,
            size: file.size,
            magic_brolls: magicBrolls,
            magic_brolls_pct: magicBrollsPct,
            remove_silence: opts.removeSilence,
            silence_pace: opts.silencePace,
            template_name: opts.templateName,
        });
    };

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) { e.target.value = ""; return; }
        // WO#9 defense-in-depth: run the readiness gate BEFORE any side effect.
        // Never discard the already-selected File on failure -> do NOT reset the
        // input value; the same file remains available once music becomes ready.
        if (!uploadReadiness.ok) { announceBlocked(); return; }
        e.target.value = "";   // safe to reset only now that we will proceed
        // Derive routing from the B-roll toggle at click time.
        const folder = brollOn ? "auto-broll" : "no-broll";
        const magicBrolls = brollOn;
        const magicBrollsPct = brollPct;
        const opts = {
            removeSilence, silencePace, templateName: captionStyle,
            music: {
                enabled: musicEnabled,
                userMediaId: musicEnabled ? musicSelectedId : null,
                volume: musicVolume,
            },
        };
        setUploading({ folder, progress: 0 });
        try {
            try {
                await directUpload(folder, file, magicBrolls, magicBrollsPct, opts);
            } catch (directErr) {
                // Safety net: if the direct-to-R2 path fails, fall back to the
                // original streaming route (still live on the backend).
                console.warn(
                    "Direct upload failed — falling back to streaming route",
                    directErr,
                );
                setUploading({ folder, progress: 0 });
                await streamingUpload(folder, file, magicBrolls, magicBrollsPct, opts);
            }
            toast.success(`"${file.name}" uploaded — processing started.`);
            fetchVideos();
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Upload failed. Try again.",
            );
        } finally {
            setUploading(null);
        }
    };

    return (
        <div className="relative min-h-screen overflow-hidden bg-black text-white">
            <NeonBackground intensity={live ? 1 : 0.35} />

            {/* Top navigation (account controls now live on the Account page) */}
            <AppShell
                active="dashboard"
                onLogoClick={() => navigate("/")}
                onNavigate={(key) =>
                    navigate(
                        key === "videos"
                            ? "/videos"
                            : key === "account"
                              ? "/account"
                              : "/dashboard",
                    )
                }
            >

            <main className="relative z-10 mx-auto w-full max-w-6xl px-4 py-8 sm:px-6">
                {(workspaceGate === "workspace" || wizardOpen) && (
                <>
                <div className="mb-2">
                    <h1 className="rf-display text-4xl uppercase sm:text-5xl lg:text-6xl">
                        Welcome, {user?.full_name?.split(" ")[0] || "creator"}.
                    </h1>
                </div>

                {/* Account setup (waiting room) banner */}
                <AnimatePresence>
                    {locked && (
                        <motion.div
                            data-testid="account-setup-banner"
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, height: 0, marginTop: 0 }}
                            className="mt-6 overflow-hidden rounded-lg border border-purple-400/30 bg-purple-500/[0.07] p-6 sm:p-8"
                        >
                            <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                                <span className="flex h-12 w-12 flex-none items-center justify-center rounded-lg border border-purple-400/40 bg-purple-500/10">
                                    <Clapperboard className="h-6 w-6 text-purple-200" />
                                </span>
                                <div>
                                    <p className="rf-heading text-2xl font-bold text-purple-100 sm:text-3xl">
                                        We're setting up your account!
                                    </p>
                                    <p className="mt-1 max-w-2xl text-base text-purple-100/75 sm:text-lg">
                                        Your ReelFlo workspace is being
                                        personalized. You'll have full access
                                        soon — we'll be in touch shortly.
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Customer usage meter — sits above the workspace controls.
                    Self-contained: a load failure never blocks upload/record. */}
                <UsageMeter usage={usage} error={usageError} />

                {/* Workflow presets — save/apply the current workspace setup.
                    Applying only updates local UI state (never uploads/reserves). */}
                <WorkflowPresetControl
                    settings={currentSettings}
                    onApply={applyPreset}
                    disabled={!live || !!uploading}
                    defaultTemplateId={user?.default_template_id || null}
                    onDefaultChanged={refreshUser}
                    reloadSignal={presetReloadSignal}
                    compact={inCompactShell}
                    onActiveChange={setActiveTemplate}
                    onInlineEdit={() => {
                        // Hydrate the SAVED snapshot before the editor renders;
                        // refuse (bounded error, zero mutation) when invalid.
                        const snap = activeTemplate?.settings;
                        if (!hydrateSavedSnapshot(snap)) {
                            toast.error(EDIT_SNAPSHOT_LOAD_ERROR);
                            return;
                        }
                        setEditBaseline(Object.freeze({ ...snap }));
                        setSaveEditsError("");
                        setTemplateEditing(true);
                    }}
                    saveEditsSignal={saveEditsSignal}
                    onSaveEditsResult={(res) => {
                        setSaveEditsBusy(false);
                        if (res?.ok) {
                            setSaveEditsError("");
                            setTemplateEditing(false);
                            setEditBaseline(null);
                            // Bump so the presets list reloads with the new revision.
                            setPresetReloadSignal((n) => n + 1);
                        } else {
                            // Keep edit view open with unsaved values preserved.
                            setSaveEditsError(res?.message || "Could not save changes. Please try again.");
                        }
                    }}
                    wizardCatalog={{
                        captionStyles: CAPTION_STYLES,
                        customCaptionSelection: CUSTOM_CAPTION_SELECTION,
                        customCaptionAvailable: !!user?.custom_theme_available,
                        musicTracks: availableMusicTracks,
                        musicLoading: musicStatus.loading,
                        musicError: musicStatus.error,
                        musicLoadedOnce: musicStatus.loadedOnce,
                        onRequestMusicLoad: ensureMusicCatalog,
                    }}
                />

                {/* Action grid (power-on saturation) */}
                <div
                    className="relative mt-6"
                    style={{
                        filter: live ? "saturate(1)" : "saturate(0.45)",
                        opacity: live ? 1 : 0.9,
                        transition: "filter 0.7s ease, opacity 0.7s ease",
                    }}
                >
                    <div className="relative z-10 flex flex-col gap-5 sm:gap-6">
                        {/* Edit view header — restrained "Unsaved changes" copy
                            (no yellow badge). Sticky Save / Cancel bar renders
                            further down, immediately below the last detail
                            section, so it stays adjacent to the controls it
                            saves. */}
                        {inCompactShell && templateEditing && (
                            <div
                                data-testid="edit-view-banner"
                                className="flex flex-col gap-1 rounded-lg border border-purple-400/30 bg-surface-raised p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5"
                            >
                                <div className="min-w-0">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-purple-200/80">
                                        Editing template
                                    </p>
                                    <p
                                        data-testid="edit-view-template-name"
                                        className="rf-heading truncate text-lg font-bold text-white sm:text-xl"
                                        title={activeTemplate?.name || ""}
                                    >
                                        {activeTemplate?.name || "—"}
                                    </p>
                                </div>
                                {editDirty && (
                                    <p
                                        data-testid="edit-view-dirty-indicator"
                                        className="text-sm text-white/60"
                                    >
                                        Unsaved changes
                                    </p>
                                )}
                            </div>
                        )}
                        {showDetailControls && (
                        <>
                        {/* WO#24: real Submagic B-roll on/off toggle */}
                        <div
                            data-testid="broll-toggle-row"
                            className="flex items-center justify-between gap-4 rounded-lg border border-white/12 bg-surface-panel p-5 sm:p-6"
                        >
                            <div className="flex items-center gap-4 sm:gap-5">
                                <div
                                    className="flex h-12 w-12 flex-none items-center justify-center rounded-lg border sm:h-14 sm:w-14"
                                    style={{
                                        borderColor: brollOn
                                            ? "#A855F766"
                                            : "#ffffff1f",
                                        background: brollOn
                                            ? "#A855F71f"
                                            : "#ffffff0d",
                                    }}
                                >
                                    <Clapperboard
                                        className="h-6 w-6 sm:h-7 sm:w-7"
                                        style={{
                                            color: brollOn
                                                ? "#A855F7"
                                                : "#ffffff80",
                                        }}
                                    />
                                </div>
                                <div className="min-w-0">
                                    <p className="rf-heading text-xl font-bold leading-tight text-white sm:text-2xl">
                                        Auto B-Roll
                                    </p>
                                    <p
                                        data-testid="broll-toggle-state-label"
                                        className="mt-1 text-sm text-white/55 sm:text-base"
                                    >
                                        {brollOn
                                            ? "On — Submagic adds AI b-roll to your video"
                                            : "Off — clean cut, no b-roll added"}
                                    </p>
                                </div>
                            </div>
                            <Switch
                                data-testid="broll-toggle-switch"
                                checked={brollOn}
                                onCheckedChange={setBrollOn}
                                disabled={!live || !!uploading}
                                aria-label="Toggle Auto B-Roll"
                                className="data-[state=checked]:bg-purple-500 data-[state=unchecked]:bg-white/15"
                            />
                        </div>

                        {/* WO#24b: B-Roll amount — shown ONLY when toggle is ON */}
                        <AnimatePresence initial={false}>
                            {brollOn && (
                                <motion.div
                                    key="broll-amount"
                                    data-testid="broll-amount-selector"
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.22, ease: "easeOut" }}
                                    className="overflow-hidden"
                                >
                                    <div className="flex flex-col gap-4 rounded-lg border border-white/12 bg-surface-panel p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
                                        <div className="min-w-0">
                                            <p className="text-sm font-semibold uppercase tracking-wide text-white/70 sm:text-base">
                                                B-Roll Amount
                                            </p>
                                            <p className="mt-0.5 text-sm text-white/45">
                                                How much AI b-roll to add
                                            </p>
                                        </div>
                                        <div
                                            role="group"
                                            aria-label="B-Roll amount"
                                            className="grid grid-cols-3 gap-1.5 rounded-full border border-white/10 bg-white/[0.04] p-1"
                                        >
                                            {[25, 50, 75].map((v) => {
                                                const active = brollPct === v;
                                                return (
                                                    <button
                                                        key={v}
                                                        type="button"
                                                        data-testid={`broll-amount-${v}`}
                                                        onClick={() => setBrollPct(v)}
                                                        disabled={!live || !!uploading}
                                                        aria-pressed={active}
                                                        className={[
                                                            "min-h-11 rounded-full px-5 text-sm font-semibold transition-colors sm:text-base",
                                                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/60",
                                                            "disabled:cursor-not-allowed disabled:opacity-50",
                                                            active
                                                                ? "bg-purple-600 text-white"
                                                                : "text-white/60 hover:bg-white/[0.06] hover:text-white",
                                                        ].join(" ")}
                                                    >
                                                        {v}%
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* WO#24c: Pause / Dead-Air Removal toggle */}
                        <div
                            data-testid="silence-toggle-row"
                            className="flex items-center justify-between gap-4 rounded-lg border border-white/12 bg-surface-panel p-5 sm:p-6"
                        >
                            <div className="flex items-center gap-4 sm:gap-5">
                                <div
                                    className="flex h-12 w-12 flex-none items-center justify-center rounded-lg border sm:h-14 sm:w-14"
                                    style={{
                                        borderColor: removeSilence
                                            ? "#22D3EE66"
                                            : "#ffffff1f",
                                        background: removeSilence
                                            ? "#22D3EE1f"
                                            : "#ffffff0d",
                                    }}
                                >
                                    <Scissors
                                        className="h-6 w-6 sm:h-7 sm:w-7"
                                        style={{
                                            color: removeSilence
                                                ? "#22D3EE"
                                                : "#ffffff80",
                                        }}
                                    />
                                </div>
                                <div className="min-w-0">
                                    <p className="rf-heading text-xl font-bold leading-tight text-white sm:text-2xl">
                                        Pause / Dead-Air Removal
                                    </p>
                                    <p
                                        data-testid="silence-toggle-state-label"
                                        className="mt-1 text-sm text-white/55 sm:text-base"
                                    >
                                        {removeSilence
                                            ? "On — removes silences and bad takes for a tighter cut"
                                            : "Off — keeps your original pacing, no cuts"}
                                    </p>
                                </div>
                            </div>
                            <Switch
                                data-testid="silence-toggle-switch"
                                checked={removeSilence}
                                onCheckedChange={setRemoveSilence}
                                disabled={!live || !!uploading}
                                aria-label="Toggle Pause and Dead-Air Removal"
                                className="data-[state=checked]:bg-cyan-400 data-[state=unchecked]:bg-white/15"
                            />
                        </div>

                        {/* WO#24c: removal pace — shown ONLY when toggle is ON */}
                        <AnimatePresence initial={false}>
                            {removeSilence && (
                                <motion.div
                                    key="silence-pace"
                                    data-testid="silence-pace-selector"
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.22, ease: "easeOut" }}
                                    className="overflow-hidden"
                                >
                                    <div className="flex flex-col gap-4 rounded-lg border border-white/12 bg-surface-panel p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
                                        <div className="min-w-0">
                                            <p className="text-sm font-semibold uppercase tracking-wide text-white/70 sm:text-base">
                                                Removal Pace
                                            </p>
                                            <p className="mt-0.5 text-sm text-white/45">
                                                How aggressively to cut pauses
                                            </p>
                                        </div>
                                        <div
                                            role="group"
                                            aria-label="Silence removal pace"
                                            className="grid grid-cols-3 gap-1.5 rounded-full border border-white/10 bg-white/[0.04] p-1"
                                        >
                                            {[
                                                { v: "natural", label: "Natural" },
                                                { v: "fast", label: "Fast" },
                                                { v: "extra-fast", label: "Extra-Fast" },
                                            ].map(({ v, label }) => {
                                                const active = silencePace === v;
                                                return (
                                                    <button
                                                        key={v}
                                                        type="button"
                                                        data-testid={`silence-pace-${v}`}
                                                        onClick={() => setSilencePace(v)}
                                                        disabled={!live || !!uploading}
                                                        aria-pressed={active}
                                                        className={[
                                                            "min-h-11 rounded-full px-4 text-sm font-semibold transition-colors sm:text-base",
                                                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/60",
                                                            "disabled:cursor-not-allowed disabled:opacity-50",
                                                            active
                                                                ? "bg-cyan-600 text-white"
                                                                : "text-white/60 hover:bg-white/[0.06] hover:text-white",
                                                        ].join(" ")}
                                                    >
                                                        {label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* WO#8: curated 8-style caption picker */}
                        <CaptionStylePicker
                            value={captionStyle}
                            onChange={setCaptionStyle}
                            disabled={!live || !!uploading}
                            customThemeAvailable={!!user?.custom_theme_available}
                            customThemeName={user?.custom_theme_name || ""}
                        />

                        {/* WO#9: optional background-music picker */}
                        <div
                            ref={musicSectionRef}
                            id="music-picker-section"
                            tabIndex={-1}
                            className="outline-none"
                        >
                            <MusicPicker
                                enabled={musicEnabled}
                                onEnabledChange={setMusicEnabled}
                                selectedId={musicSelectedId}
                                onSelect={setMusicSelectedId}
                                volume={musicVolume}
                                onVolumeChange={setMusicVolume}
                                onStatusChange={setMusicStatus}
                                tracks={availableMusicTracks}
                                loading={musicStatus.loading}
                                loadingMore={musicLoadingMore}
                                error={musicErrorDetail}
                                loadedOnce={musicStatus.loadedOnce}
                                hasMore={musicHasMore}
                                onEnsure={ensureMusicCatalog}
                                onLoadMore={loadMore}
                                onRefresh={refreshMusicLibrary}
                                resetSignal={musicRefreshSeq}
                                disabled={!live || !!uploading}
                            />
                        </div>
                        </>
                        )}

                        {/* Compact-Dashboard edit-view sticky Save / Cancel bar.
                            Save triggers the existing owner-scoped revision-aware
                            PATCH inside WorkflowPresetControl via saveEditsSignal.
                            Cancel reapplies the saved template snapshot. Both
                            leave the file picker closed and preserve unsaved
                            values on failure. */}
                        {inCompactShell && templateEditing && (
                            <div
                                data-testid="edit-view-actions"
                                className="flex flex-col gap-3 rounded-lg border border-white/12 bg-surface-raised p-4 sm:p-5"
                            >
                                {saveEditsError && (
                                    <p
                                        data-testid="edit-view-save-error"
                                        role="alert"
                                        className="text-sm text-amber-200/90"
                                    >
                                        {saveEditsError}
                                    </p>
                                )}
                                <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                                    <button
                                        type="button"
                                        data-testid="edit-view-cancel"
                                        onClick={() => {
                                            if (saveEditsBusy) return;
                                            // Direct baseline restoration — never
                                            // applyPreset, never a PATCH. Close only
                                            // after a successful restore; otherwise
                                            // keep the editor open, mutate nothing.
                                            if (!hydrateSavedSnapshot(editBaseline)) {
                                                setSaveEditsError(EDIT_SNAPSHOT_LOAD_ERROR);
                                                return;
                                            }
                                            setSaveEditsError("");
                                            setEditBaseline(null);
                                            setTemplateEditing(false);
                                        }}
                                        disabled={saveEditsBusy}
                                        className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-black/40 min-h-11 px-5 text-sm font-semibold text-white/85 transition-colors hover:border-white/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/60 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        data-testid="edit-view-save"
                                        onClick={() => {
                                            if (saveEditsBusy || !activeTemplate) return;
                                            setSaveEditsError("");
                                            setSaveEditsBusy(true);
                                            setSaveEditsSignal((n) => n + 1);
                                        }}
                                        disabled={saveEditsBusy || !activeTemplate}
                                        className="inline-flex items-center justify-center gap-2 rounded-full border border-purple-400/50 bg-purple-500/20 min-h-11 px-5 text-sm font-semibold text-purple-100 transition-colors hover:bg-purple-500/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/60 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        {saveEditsBusy ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : null}
                                        Save changes
                                    </button>
                                </div>
                            </div>
                        )}

                        <div
                            ref={uploadSectionRef}
                            id="upload-section"
                            tabIndex={-1}
                            className="scroll-mt-24 outline-none"
                        >
                            {/* Compact-Dashboard edit-view lockout: wrap the
                                upload ActionButton in a `disabled` fieldset so
                                the button becomes disabled while the customer
                                is editing their template. The exact readiness
                                expression on ActionButton itself is preserved
                                (see tests/music_picker_test.py). */}
                            <fieldset
                                data-testid="upload-edit-lockout"
                                disabled={templateEditing}
                                className={
                                    "m-0 min-w-0 border-0 p-0" +
                                    (templateEditing ? " opacity-60" : "")
                                }
                            >
                            <ActionButton
                                testId="dashboard-upload-button"
                                icon={Upload}
                                title="Upload Your Video"
                                subtitle={
                                    inCompactShell
                                        ? undefined
                                        : brollOn
                                            ? "Auto B-Roll is ON — AI adds b-roll"
                                            : "Auto B-Roll is OFF — clean cut only"
                                }
                                accent="#A855F7"
                                disabled={!live || !!uploading || !uploadReadiness.ok}
                                breathe={
                                    live && !uploading && uploadReadiness.ok && !templateEditing
                                }
                                ariaDescribedBy={
                                    !uploadReadiness.ok ? "upload-blocked-reason" : undefined
                                }
                                onClick={pick}
                                uploading={!!uploading}
                                progress={uploading?.progress || 0}
                            />
                            </fieldset>
                            {!uploadReadiness.ok && uploadReadiness.reason && (
                                <p
                                    id="upload-blocked-reason"
                                    role="alert"
                                    data-testid="upload-blocked-reason"
                                    className="mt-3 text-center text-sm text-amber-200/90"
                                >
                                    {uploadReadiness.reason}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* hidden file input (single) */}
                <input
                    ref={fileRef}
                    type="file"
                    accept="video/*"
                    className="hidden"
                    data-testid="upload-input"
                    onChange={handleUpload}
                />

                {/* Processing tray — temporary, secondary. Holds every video
                    that has NOT been confirmed handed off to Finished Videos,
                    PLUS any handed-off video whose completion has not yet been
                    acknowledged (new_completion === true). A card leaves only
                    when it is ready AND acknowledged via the CTA. Renders
                    nothing when empty. */}
                <ProcessingTray
                    videos={videos}
                    celebrationId={celebrationId}
                    onUploadAgain={scrollToUpload}
                    onViewFinished={handleViewFinished}
                    containerRef={historyRef}
                />
                </>
                )}

                {/* Not-Yet onboarding states — while templates load we show
                    NEITHER the workspace NOR the pre-template screen (no flash);
                    a failed load surfaces an error + Retry (Retry re-requests ONLY
                    the preset list); a successful load with zero templates shows
                    the simple pre-template screen. A customer with >= 1 template
                    keeps the full workspace. */}
                {workspaceGate === "loading" && !wizardOpen && (
                    <div
                        data-testid="workspace-loading"
                        className="flex items-center justify-center py-24 text-white/40"
                    >
                        <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                )}

                {workspaceGate === "error" && !wizardOpen && (
                    <div
                        data-testid="pre-template-error"
                        className="mx-auto mt-10 flex max-w-md flex-col items-center gap-4 rounded-lg border border-amber-400/30 bg-amber-400/[0.06] p-8 text-center"
                    >
                        <AlertTriangle className="h-8 w-8 text-amber-300" />
                        <p className="text-base text-amber-100/90">
                            We couldn't load your templates.
                        </p>
                        <button
                            type="button"
                            data-testid="pre-template-retry"
                            onClick={loadPresetGate}
                            className="inline-flex items-center gap-2 rounded-full border border-white/15 min-h-11 px-5 text-sm font-semibold text-white/80 transition-colors duration-200 hover:border-purple-400/50 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/60"
                        >
                            <RefreshCw className="h-4 w-4" /> Retry
                        </button>
                    </div>
                )}

                {workspaceGate === "pretemplate" && !wizardOpen && (
                    <div
                        data-testid="pre-template-screen"
                        className="mx-auto mt-10 flex max-w-lg flex-col items-center gap-5 rounded-lg border border-white/12 bg-surface-panel p-10 text-center"
                    >
                        <span className="flex h-14 w-14 items-center justify-center rounded-lg border border-purple-400/40 bg-purple-500/10">
                            <Rocket className="h-7 w-7 text-purple-200" />
                        </span>
                        <h2
                            data-testid="pre-template-headline"
                            className="rf-heading text-2xl font-bold text-white sm:text-3xl"
                        >
                            Your first video starts when you're ready.
                        </h2>
                        <p className="max-w-sm text-base text-white/60">
                            Set up a reusable editing template once, and every upload comes
                            out exactly how you like it.
                        </p>
                        <button
                            type="button"
                            data-testid="pre-template-start-button"
                            onClick={() => setWizardOpen(true)}
                            className="inline-flex items-center gap-2 rounded-full border border-purple-400/50 bg-purple-500/20 min-h-11 px-6 text-sm font-semibold text-purple-100 transition-colors duration-200 hover:bg-purple-500/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/60"
                        >
                            <Rocket className="h-4 w-4" /> Set up my first video
                        </button>
                    </div>
                )}

                {/* First-run welcome prompt — the single gate to onboarding.
                    wizard_seen is persisted the moment the creator answers (Yes
                    OR No) and BEFORE this modal closes; a save failure keeps it
                    open with a Retry that repeats only that write. */}
                {welcomeOpen && (
                    <div
                        data-testid="welcome-modal"
                        role="dialog"
                        aria-modal="true"
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
                    >
                        <div className="relative w-full max-w-md rounded-lg border border-white/12 bg-surface-raised p-6 text-center sm:p-8">
                            <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-lg border border-purple-400/40 bg-purple-500/10">
                                <Rocket className="h-7 w-7 text-purple-200" />
                            </span>
                            <h2 className="rf-heading text-2xl font-bold text-white sm:text-3xl">
                                Are you ready to upload your first video?
                            </h2>
                            <p className="mt-2 text-sm text-white/60 sm:text-base">
                                We'll build a reusable template so every upload comes
                                out exactly how you like it.
                            </p>
                            {welcomeError && (
                                <p
                                    data-testid="welcome-error"
                                    role="alert"
                                    className="mt-4 text-sm text-amber-200/90"
                                >
                                    We couldn't save that just now. Please retry.
                                </p>
                            )}
                            <div className="mt-6 flex flex-col gap-3">
                                {welcomeError ? (
                                    <button
                                        type="button"
                                        data-testid="welcome-retry"
                                        onClick={retryWizardSeen}
                                        disabled={welcomeSaving}
                                        className="inline-flex items-center justify-center gap-2 rounded-full border border-purple-400/50 bg-purple-500/20 min-h-11 px-5 text-sm font-semibold text-purple-100 disabled:opacity-50"
                                    >
                                        {welcomeSaving ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : null}
                                        Retry
                                    </button>
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            data-testid="welcome-yes"
                                            onClick={() => persistWizardSeen("yes")}
                                            disabled={welcomeSaving}
                                            className="inline-flex items-center justify-center gap-2 rounded-full border border-purple-400/50 bg-purple-500/20 min-h-11 px-5 text-sm font-semibold text-purple-100 disabled:opacity-50"
                                        >
                                            {welcomeSaving && welcomeChoice === "yes" ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : null}
                                            Yes, let's set it up
                                        </button>
                                        <button
                                            type="button"
                                            data-testid="welcome-no"
                                            onClick={() => persistWizardSeen("no")}
                                            disabled={welcomeSaving}
                                            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 min-h-11 px-5 text-sm font-semibold text-white/75 hover:text-white disabled:opacity-50"
                                        >
                                            {welcomeSaving && welcomeChoice === "no" ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : null}
                                            Not yet
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* First-run template wizard (opened only after the Yes answer's
                    wizard_seen write succeeds). Creates the first template and
                    sets it as default; never posts wizard_seen again. */}
                <FirstRunTemplateWizard
                    open={wizardOpen}
                    mode="create"
                    initialSettings={currentSettings}
                    existingPresets={[]}
                    captionStyles={CAPTION_STYLES}
                    customCaptionSelection={CUSTOM_CAPTION_SELECTION}
                    customCaptionAvailable={!!user?.custom_theme_available}
                    musicTracks={availableMusicTracks}
                    musicLoading={musicStatus.loading}
                    musicError={musicStatus.error}
                    musicLoadedOnce={musicStatus.loadedOnce}
                    onRequestMusicLoad={ensureMusicCatalog}
                    onCancel={() => setWizardOpen(false)}
                    onFinish={handleFirstRunFinish}
                    onRetryDefault={handleFirstRunRetryDefault}
                />
            </main>
            </AppShell>
        </div>
    );
}


// Named export of the presentational caption-style picker for focused offline
// tests. Does not affect the default page export or any runtime behavior.
export { CaptionStylePicker, CUSTOM_CAPTION_SELECTION, validatePresetSnapshotV1, PRESET_SCHEMA_VERSION };
